import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import SignatureCanvas from 'react-signature-canvas';
import { Building2, CheckCircle2, Clock, Loader2, AlertCircle, PenTool, RotateCcw } from 'lucide-react';
import SEPAPaymentForm from '../components/payment/SEPAPaymentForm';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public page used by clients to sign a SEPA mandate triggered by the admin
 * from a manual order. The URL is `/sepa-sign/:requestId` and is unguessable.
 */
const SEPASignPage = () => {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signatureData, setSignatureData] = useState(null);
  const sigPadRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const fetchPayload = async () => {
      try {
        const r = await axios.get(`${API}/sepa/sign/${requestId}`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || 'Lien invalide ou expiré');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchPayload();
    return () => { cancelled = true; };
  }, [requestId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-zinc-900 to-black flex items-center justify-center text-white">
        <Loader2 className="w-8 h-8 animate-spin text-yellow-400" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-zinc-900 to-black flex items-center justify-center p-6">
        <div className="bg-zinc-900 border border-red-500/30 rounded-2xl p-8 max-w-md text-center text-white">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
          <h2 className="text-xl font-bold mb-2">Lien invalide</h2>
          <p className="text-white/60 text-sm">{error || 'Cette demande SEPA est introuvable.'}</p>
        </div>
      </div>
    );
  }

  // Already signed → show confirmation
  if (data.status !== 'awaiting_mandate') {
    const map = {
      pending_approval: { color: 'text-yellow-400', icon: Clock, title: 'Mandat déjà signé', desc: 'Votre demande est en cours d\'examen par notre équipe (sous 48h ouvrées).' },
      approved: { color: 'text-emerald-400', icon: CheckCircle2, title: 'Demande approuvée', desc: 'Votre commande est confirmée. Le 1er prélèvement va être effectué sous 5 à 7 jours ouvrés.' },
      rejected: { color: 'text-red-400', icon: AlertCircle, title: 'Demande non validée', desc: 'Votre demande n\'a pas été approuvée. Consultez votre email pour le motif.' },
    };
    const info = map[data.status] || map.pending_approval;
    const Icon = info.icon;
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-zinc-900 to-black flex items-center justify-center p-6 text-white">
        <div className="bg-zinc-900 border border-yellow-400/20 rounded-2xl p-8 max-w-md text-center">
          <Icon className={`w-14 h-14 ${info.color} mx-auto mb-4`} />
          <h2 className="text-2xl font-bold mb-2">{info.title}</h2>
          <p className="text-white/60 text-sm mb-6">{info.desc}</p>
          {data.iban_last4 && (
            <div className="text-xs text-white/40 font-mono">IBAN : **** **** **** {data.iban_last4}</div>
          )}
        </div>
      </div>
    );
  }

  const onSigned = (setupIntent) => {
    // Generate the official mandate PDF with the drawn signature (best-effort).
    axios.post(`${API}/sepa/checkout/mandate-signed`, {
      request_id: requestId,
      signature_base64: signatureData || '',
      setup_intent_id: setupIntent?.id || null,
    }).catch((e) => console.error('mandate-signed record failed:', e));
    navigate(`/sepa/pending/${requestId}`);
  };

  const clearSignature = () => {
    if (sigPadRef.current) sigPadRef.current.clear();
    setSignatureData(null);
  };

  const saveSignature = () => {
    if (sigPadRef.current && !sigPadRef.current.isEmpty()) {
      setSignatureData(sigPadRef.current.getTrimmedCanvas().toDataURL('image/png'));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-zinc-900 to-black text-white" data-testid="sepa-sign-page">
      <div className="max-w-xl mx-auto px-6 py-12">
        <div className="text-center mb-8">
          <div className="inline-flex w-16 h-16 rounded-full bg-yellow-400/10 border border-yellow-400/30 items-center justify-center mb-4">
            <Building2 className="w-8 h-8 text-yellow-400" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight">Signez votre mandat SEPA</h1>
          <p className="text-white/60 mt-2 text-sm">
            Bonjour {data.customer_name}, finalisez votre location en 30 secondes.
          </p>
        </div>

        <div className="bg-zinc-900 border border-yellow-400/20 rounded-2xl p-6 mb-6">
          <div className="grid grid-cols-2 gap-y-2 text-sm mb-4">
            <div className="text-white/50">Pack</div>
            <div className="text-white text-right font-semibold">{data.pack_name}</div>
            <div className="text-white/50">Mensualité</div>
            <div className="text-white text-right">{(data.monthly_price || 0).toFixed(2)} €/mois</div>
            <div className="text-white/50">1er prélèvement (au visa)</div>
            <div className="text-yellow-400 font-bold text-right">{(data.first_charge_amount || 0).toFixed(2)} €</div>
          </div>
        </div>

        <div className="bg-zinc-900 border border-yellow-400/20 rounded-2xl p-6">
          {!signatureData ? (
            <div data-testid="sepa-sign-signature-step">
              <div className="flex items-center gap-2 mb-2">
                <PenTool className="w-5 h-5 text-yellow-400" />
                <h3 className="text-lg font-bold text-white">1. Signez ci-dessous</h3>
              </div>
              <p className="text-white/60 text-sm mb-4">
                Dessinez votre signature. Elle sera apposée sur votre mandat de prélèvement SEPA.
              </p>
              <div className="bg-white rounded-lg overflow-hidden">
                <SignatureCanvas
                  ref={sigPadRef}
                  penColor="black"
                  canvasProps={{ className: 'w-full', style: { width: '100%', height: '180px' } }}
                />
              </div>
              <div className="flex gap-3 mt-3">
                <button
                  type="button"
                  onClick={clearSignature}
                  className="flex-1 py-2.5 rounded-lg border border-white/15 text-white/80 hover:bg-white/5 font-semibold transition flex items-center justify-center gap-2"
                  data-testid="sepa-sign-clear"
                >
                  <RotateCcw className="w-4 h-4" /> Effacer
                </button>
                <button
                  type="button"
                  onClick={saveSignature}
                  className="flex-1 py-2.5 rounded-lg bg-yellow-400 hover:bg-yellow-300 text-black font-bold transition"
                  data-testid="sepa-sign-validate"
                >
                  Valider ma signature
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                  <h3 className="text-lg font-bold text-white">2. Renseignez votre IBAN</h3>
                </div>
                <button
                  type="button"
                  onClick={clearSignature}
                  className="text-xs text-white/50 hover:text-white underline"
                  data-testid="sepa-sign-redo"
                >
                  Refaire ma signature
                </button>
              </div>
              <SEPAPaymentForm
                clientSecret={data.client_secret}
                customerName={data.customer_name}
                customerEmail={data.customer_email}
                firstChargeAmount={data.first_charge_amount || 0}
                signatureImage={signatureData}
                onMandateSigned={onSigned}
                onCancel={() => window.history.back()}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SEPASignPage;
